import type { EntitlementKey } from '../api/entitlements';

// ---------------------------------------------------------------------------
// The one table of plan feature switches. A BillingProduct and a Tenant carry
// the same `<entitlement>Enabled` booleans, so the platform product editor,
// the platform tenant editor and the Organisation page all read this table:
// a feature can't be switchable in one place and missing in another. The EA
// workspace is not a switch: a plan or organisation has it when it has EA
// seats.
// ---------------------------------------------------------------------------

/** Every entitlement that is its own on/off switch. */
export type SwitchableEntitlement = Exclude<EntitlementKey, 'eaWorkspace'>;

/** The field on BillingProduct and Tenant, e.g. "strategicDriversEnabled". */
export type FeatureFlagField = `${SwitchableEntitlement}Enabled`;

export type FeatureGroup = 'Core' | 'Leadership tools' | 'Professional' | 'Enterprise';

export interface FeatureFlagDef {
  key: SwitchableEntitlement;
  field: FeatureFlagField;
  label: string;
  group: FeatureGroup;
}

export const FEATURE_GROUPS: readonly FeatureGroup[] = ['Core', 'Leadership tools', 'Professional', 'Enterprise'];

const flag = (key: SwitchableEntitlement, label: string, group: FeatureGroup): FeatureFlagDef =>
  ({ key, field: `${key}Enabled`, label, group });

// Grouped as the plans sell them (PLAN-TIERS spec section 2); any feature can
// still be switched on for any plan.
export const FEATURE_FLAGS: readonly FeatureFlagDef[] = [
  flag('aiPlanning', 'AI planning', 'Core'),
  flag('voiceCapture', 'Voice capture', 'Core'),
  flag('projects', 'Projects', 'Core'),
  flag('strategicDrivers', 'Strategic drivers & KPIs', 'Leadership tools'),
  flag('decisions', 'Decisions', 'Leadership tools'),
  flag('delegation', 'Delegation', 'Leadership tools'),
  flag('peopleHistory', 'People history', 'Leadership tools'),
  flag('principalProfile', 'Principal profiles', 'Leadership tools'),
  flag('capacityPlanning', 'Capacity planning', 'Leadership tools'),
  flag('transcriptProcessing', 'Transcript processing', 'Professional'),
  flag('imageAnalysis', 'Image-to-task', 'Professional'),
  flag('pdfExport', 'PDF export', 'Professional'),
  flag('webhooks', 'Webhooks', 'Professional'),
  flag('sso', 'SSO', 'Professional'),
  flag('advancedReporting', 'Advanced reporting', 'Professional'),
  flag('tenantAiKey', 'Own AI key', 'Professional'),
  flag('apiAccess', 'API access', 'Enterprise'),
  flag('crossExecutiveReporting', 'Cross-executive reporting', 'Enterprise'),
  flag('customBranding', 'Custom branding', 'Enterprise'),
  flag('dedicatedOnboarding', 'Dedicated onboarding', 'Enterprise'),
  flag('prioritySupport', 'Priority support', 'Enterprise'),
];

/** The table in its groups, in order, for rendering under headings. */
export const FEATURE_FLAG_GROUPS: readonly { group: FeatureGroup; flags: readonly FeatureFlagDef[] }[] =
  FEATURE_GROUPS.map(group => ({ group, flags: FEATURE_FLAGS.filter(f => f.group === group) }));

/** The six leadership tools, each separately switchable per plan. */
export const LEADERSHIP_TOOLS: readonly SwitchableEntitlement[] =
  FEATURE_FLAGS.filter(f => f.group === 'Leadership tools').map(f => f.key);

/** The switches on before and off after: what an organisation would lose. */
export function flagsSwitchedOff(
  before: Partial<Record<FeatureFlagField, boolean | undefined>>,
  after: Partial<Record<FeatureFlagField, boolean | undefined>>,
): FeatureFlagDef[] {
  return FEATURE_FLAGS.filter(f => !!before[f.field] && !after[f.field]);
}

/** "A", "A and B", "A, B and C". */
export function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
